/**
 * Veluna Endpoint Gating Layer
 * 
 * Prevents 404 spam when AWS/Jarvis endpoints are not deployed.
 * Caches endpoint availability in localStorage with TTL.
 * 
 * SINGLE SOURCE OF TRUTH: Luna chat endpoint is read from window.LunaConfig.proxyEndpoint
 * (same source as veluna-luna-chatbot.js uses) to prevent endpoint mismatches.
 * 
 * Usage:
 *   const available = await velunaEndpointAvailable('/apps/jarvis/email/cart-abandonment');
 *   if (available) {
 *     // Make API call
 *   }
 */

(function() {
  'use strict';

  // BOOT PROOF: Log that script loaded
  console.log('[VELUNA_GATE] LOADED', { ts: new Date().toISOString() });
  
  // SINGLE SOURCE OF TRUTH: Get Luna chat endpoint from same source as Luna script
  // This prevents endpoint mismatches between gate and Luna chatbot
  const LUNA_CHAT_ENDPOINT = (window.LunaConfig && window.LunaConfig.proxyEndpoint) 
    ? window.LunaConfig.proxyEndpoint 
    : '/apps/jarvis/luna/api/chat';

  // Storage key prefix
  const STORAGE_PREFIX = 'veluna_endpoint_';
  
  // Initialize session log map
  if (!window.__VELUNA_GATE_LOGGED) {
    window.__VELUNA_GATE_LOGGED = {};
  }

  /**
   * Check if an endpoint is available
   * 
   * @param {string} path - Endpoint path (e.g., '/apps/jarvis/email/cart-abandonment')
   * @param {number} ttlMs - Time-to-live in milliseconds (default: 1 hour)
   * @returns {Promise<boolean>} - true if available, false if not
   */
  async function velunaEndpointAvailable(path, ttlMs) {
    // Default TTL: 1 hour
    ttlMs = ttlMs || 3600000;
    
    // If path is already a full path starting with '/', use it as-is
    // Otherwise normalize (remove leading/trailing slashes)
    let fullPath;
    if (path && path.startsWith('/')) {
      fullPath = path; // Already a full path
    } else {
      // Normalize path (remove leading/trailing slashes for consistency)
      path = path ? path.replace(/^\/+|\/+$/g, '') : '';
      if (!path) {
        return false;
      }
      // Reconstruct full path
      fullPath = '/' + path;
    }
    
    // LUNA_CHAT_ENDPOINT fix: For chat endpoints, prefer OPTIONS over HEAD
    // POST /apps/jarvis/luna/api/chat is the correct endpoint (not HEAD)
    // SINGLE SOURCE OF TRUTH: Use LUNA_CHAT_ENDPOINT constant (read from window.LunaConfig)
    // If checking Luna chat endpoint, don't use HEAD (it may return 404 for HEAD but work for POST)
    const isLunaChatEndpoint = fullPath === LUNA_CHAT_ENDPOINT || 
                               fullPath.includes(LUNA_CHAT_ENDPOINT);
    
    // DEV-ONLY: Log endpoint check (guarded by LUNA_DEBUG)
    if (window.LUNA_DEBUG === true) {
      console.log('[LUNA_DEBUG] [GATE] Checking endpoint:', fullPath, 'isLunaChat:', isLunaChatEndpoint);
    }
    
    // Generate storage key from fullPath (consistent across path formats)
    const pathForStorage = fullPath.replace(/^\/+|\/+$/g, ''); // Remove leading/trailing slashes for storage
    const storageKey = STORAGE_PREFIX + pathForStorage.replace(/[^a-zA-Z0-9]/g, '_');
    
    try {
      // Check localStorage cache
      if (typeof localStorage !== 'undefined') {
        const cached = localStorage.getItem(storageKey);
        if (cached) {
          try {
            const data = JSON.parse(cached);
            const now = Date.now();
            
            // Check if cache is still valid
            if (data.ts && (now - data.ts) < ttlMs) {
              return data.available === true;
            }
          } catch (e) {
            // Invalid cache, continue to fetch
          }
        }
      }
      
      // Cache expired or doesn't exist - check endpoint
      // For Luna chat endpoint, use OPTIONS or POST (HEAD may not be supported)
      // For other endpoints, use HEAD request (lighter than GET)
      let response;
      const method = isLunaChatEndpoint ? 'OPTIONS' : 'HEAD'; // OPTIONS for CORS preflight, works for most endpoints
      
      try {
        response = await fetch(fullPath, {
          method: method,
          credentials: 'same-origin',
          cache: 'no-cache',
          // For OPTIONS, don't send body
          ...(method === 'OPTIONS' ? {} : {})
        });
      } catch (error) {
        // Network error - treat as unavailable
        const result = false;
        cacheResult(storageKey, result);
        logOnce(fullPath, false, 'network error');
        
        // DEV-ONLY: Log gate check failure
        if (window.LUNA_DEBUG === true) {
          console.log('[LUNA_DEBUG] [GATE] Network error checking', fullPath, error.message);
        }
        
        return result;
      }
      
      // Check response status
      // For OPTIONS (CORS preflight), 200/204/405 (Method Not Allowed) means endpoint exists
      // For HEAD, 200/204 means available
      const status = response.status;
      const available = isLunaChatEndpoint 
        ? (status === 200 || status === 204 || status === 405) // OPTIONS may return 405 but endpoint exists
        : (status === 200 || status === 204);
      
      // DEV-ONLY: Log gate check result
      if (window.LUNA_DEBUG === true) {
        console.log('[LUNA_DEBUG] [GATE] Endpoint check result:', fullPath, 'status:', status, 'available:', available);
      }
      
      // Cache result
      cacheResult(storageKey, available);
      
      // Log once if unavailable
      if (!available) {
        logOnce(fullPath, false, 'status ' + status);
      }
      
      return available;
      
    } catch (error) {
      // Any error - treat as unavailable, never throw
      const result = false;
      cacheResult(storageKey, result);
      logOnce(fullPath, false, 'error: ' + (error.message || 'unknown'));
      return result;
    }
  }
  
  // Explicit window export
  window.velunaEndpointAvailable = velunaEndpointAvailable;
  
  /**
   * Cache result in localStorage
   */
  function cacheResult(storageKey, available) {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(storageKey, JSON.stringify({
          ts: Date.now(),
          available: available
        }));
      }
    } catch (e) {
      // localStorage quota exceeded or disabled - ignore
    }
  }
  
  /**
   * Log once per session
   */
  function logOnce(path, available, reason) {
    if (available) {
      return; // Don't log successful checks
    }
    
    const logKey = path;
    if (window.__VELUNA_GATE_LOGGED[logKey]) {
      return; // Already logged this session
    }
    
    window.__VELUNA_GATE_LOGGED[logKey] = true;
    console.log('[VELUNA_GATE] disabled ' + path + ' (unavailable: ' + reason + ')');
  }
  
  // Export for module systems (if needed)
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = velunaEndpointAvailable;
  }
  
})();





